import { createFileRoute } from '@tanstack/react-router'
import {
    Apple,
    Battery,
    Brain,
    Dumbbell,
    Heart,
    Moon,
    Smile,
    Sparkles,
    Users,
    Zap,
} from 'lucide-react'

import { Footer, Header } from '../components/landing'
import { buildOgMeta } from '../utils/og'

const pillars = [
    {
        icon: Apple,
        title: 'Táplálkozás',
        description:
            'Orvosi és dietetikai iránymutatás alapján összeállított menü, amely nemcsak finom, de helyreállítja a szervezet egyensúlyát.',
    },
    {
        icon: Dumbbell,
        title: 'Mozgás',
        description:
            'Személyre szabott, élvezetes mozgásformák – a reggeli jógától a funkcionális tréningig –, amelyek nem kényszerből, hanem örömből mozgatnak meg.',
    },
    {
        icon: Moon,
        title: 'Alvás',
        description:
            'Megtanítjuk, hogyan regenerálódj igazán – alvásdiagnosztikai tanácsokkal, relaxációs technikákkal és esti lecsendesítő programokkal.',
    },
    {
        icon: Brain,
        title: 'Stresszkezelés',
        description:
            'Mindfulness, légzőgyakorlatok, coaching és természetben végzett programok segítenek abban, hogy visszatalálj a belső nyugalomhoz.',
    },
]

const targetAudience = [
    {
        icon: Zap,
        text: 'Akik szeretnék javítani életminőségüket, energiaszintjüket',
    },
    {
        icon: Battery,
        text: 'Akik stresszes, fárasztó életet élnek és újra töltekeznének',
    },
    {
        icon: Heart,
        text: 'Akik szeretnének orvosi-szakmai keretek között, biztonságosan életmódot váltani',
    },
    {
        icon: Smile,
        text: 'Akik hisznek abban, hogy a hosszú élet kulcsa az egyensúly',
    },
]

const AboutPage = () => {
    return (
        <div className="min-h-screen bg-off-white font-sans text-brand-dark">
            <Header />
            <main className="flex flex-col">
                <section className="relative flex items-center overflow-hidden bg-off-white py-24">
                    <div className="absolute inset-0 z-0">
                        <div className="absolute inset-0 z-10 bg-gradient-to-r from-off-white via-off-white/80 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-brand-yellow/5" />
                    </div>
                    <div className="relative z-20 mx-auto w-full max-w-[1280px] px-6 text-center">
                        <span className="mb-6 inline-block text-sm font-bold uppercase tracking-widest text-primary">
                            Rólunk
                        </span>
                        <h1 className="mb-6 text-5xl font-bold leading-tight text-brand-dark md:text-7xl">
                            Long-Y <span className="text-primary">Életmód</span>
                        </h1>
                        <p className="mx-auto max-w-xl text-xl leading-relaxed text-brand-gray">
                            ahol a hosszú, egészséges élet kezdődik.
                        </p>
                    </div>
                </section>

                <section className="mx-auto w-full max-w-[1280px] px-6 py-24">
                    <div className="mx-auto max-w-3xl text-center">
                        <p className="text-lg leading-relaxed text-brand-gray">
                            A Long-Y Életmód célja, hogy újra összhangba hozd tested, elméd és
                            életed ritmusát. Hiszünk abban, hogy a hosszú élet nem a véletlen műve –
                            hanem tudatos döntések, kiegyensúlyozott szokások és minőségi környezet
                            eredménye.
                        </p>
                    </div>
                </section>

                <section className="bg-white py-24">
                    <div className="mx-auto w-full max-w-[1280px] px-6">
                        <div className="mb-16 text-center">
                            <h2 className="mb-4 text-3xl font-bold text-brand-dark md:text-4xl">
                                Négy alappillér
                            </h2>
                            <p className="mx-auto max-w-2xl text-brand-gray">
                                Táborainkban négy alappillér mentén vezetünk végig azon az úton, ami
                                az egészségesebb, energikusabb mindennapokhoz vezet.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
                            {pillars.map((pillar) => (
                                <div
                                    key={pillar.title}
                                    className="group rounded-2xl border border-slate-200 bg-off-white p-8 transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
                                >
                                    <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                                        <pillar.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mb-3 text-xl font-bold text-brand-dark">
                                        {pillar.title}
                                    </h3>
                                    <p className="text-sm leading-relaxed text-brand-gray">
                                        {pillar.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mx-auto w-full max-w-[1280px] px-6 py-24">
                    <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
                        <div>
                            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-yellow/30 bg-brand-yellow/20 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-dark">
                                <Sparkles className="h-4 w-4 text-primary" />
                                Szakmai háttér
                            </div>
                            <h2 className="mb-6 text-3xl font-bold text-brand-dark md:text-4xl">
                                Tapasztalt szakembergárda
                            </h2>
                            <p className="mb-6 leading-relaxed text-brand-gray">
                                A Long-Y programokat egészségügyi háttérintézmény és tapasztalt
                                szakembergárda támogatja – orvosok, dietetikusok, mozgásterapeuták
                                és pszichológusok dolgoznak azon, hogy a táborban eltöltött napok
                                valódi testi-lelki megújulást hozzanak.
                            </p>
                            <p className="leading-relaxed text-brand-gray">
                                Minden résztvevő egyéni állapotfelmérésen vesz részt, hogy a program
                                pontosan illeszkedjen az igényeihez, egészségi helyzetéhez és
                                céljaihoz.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white p-10">
                            <div className="grid grid-cols-2 gap-8">
                                {[
                                    { label: 'Orvosok', icon: Heart },
                                    { label: 'Dietetikusok', icon: Apple },
                                    { label: 'Mozgásterapeuták', icon: Dumbbell },
                                    { label: 'Pszichológusok', icon: Brain },
                                ].map((item) => (
                                    <div
                                        key={item.label}
                                        className="flex flex-col items-center gap-3 text-center"
                                    >
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                                            <item.icon className="h-5 w-5" />
                                        </div>
                                        <span className="text-sm font-semibold text-brand-dark">
                                            {item.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white py-24">
                    <div className="mx-auto w-full max-w-[1280px] px-6">
                        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
                            <div className="order-2 lg:order-1">
                                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-off-white p-10">
                                    <div className="space-y-6">
                                        <div className="flex items-start gap-4">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                                                <Users className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h4 className="mb-1 font-bold text-brand-dark">
                                                    Online tanácsadás
                                                </h4>
                                                <p className="text-sm text-brand-gray">
                                                    A tábor után sem engedjük el a kezed
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-yellow/20 text-brand-dark">
                                                <Heart className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h4 className="mb-1 font-bold text-brand-dark">
                                                    Utánkövetés
                                                </h4>
                                                <p className="text-sm text-brand-gray">
                                                    Folyamatos támogatás az új szokások
                                                    fenntartásához
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                                                <Sparkles className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h4 className="mb-1 font-bold text-brand-dark">
                                                    Közösségi támogatás
                                                </h4>
                                                <p className="text-sm text-brand-gray">
                                                    Kapcsolódj hasonló gondolkodású emberekhez
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="order-1 lg:order-2">
                                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-yellow/30 bg-brand-yellow/20 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-dark">
                                    <Heart className="h-4 w-4 text-primary" />
                                    Minőségi időtöltés
                                </div>
                                <h2 className="mb-6 text-3xl font-bold text-brand-dark md:text-4xl">
                                    Több mint pár napos kikapcsolódás
                                </h2>
                                <p className="mb-6 leading-relaxed text-brand-gray">
                                    A Long-Y Életmód Tábor nem csupán pár napos kikapcsolódás, hanem
                                    egy hosszú távú életmód-program kezdete. A tábor után sem
                                    engedjük el a kezed: online tanácsadás, utánkövetés és közösségi
                                    támogatás segít abban, hogy az új szokásokat otthon is
                                    fenntartsd.
                                </p>
                                <p className="leading-relaxed text-brand-gray">
                                    Táborainkat mindig nyugodt, természetközeli környezetben
                                    rendezzük – ahol a csend, a levegő és a táj is része a
                                    gyógyulásnak. A prémium szállás, a kulináris élmények és a
                                    gondosan megtervezett napi ritmus együttesen teremti meg azt a
                                    közeget, ahol a résztvevők valóban új energiákat és tudásanyagot
                                    gyűjthetnek.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mx-auto w-full max-w-[1280px] px-6 py-24">
                    <div className="mb-16 text-center">
                        <h2 className="mb-4 text-3xl font-bold text-brand-dark md:text-4xl">
                            Kiknek ajánljuk?
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {targetAudience.map((item) => (
                            <div
                                key={item.text}
                                className="flex items-center gap-5 rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:border-primary/30 hover:shadow-md"
                            >
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <item.icon className="h-5 w-5" />
                                </div>
                                <p className="font-medium text-brand-dark">{item.text}</p>
                            </div>
                        ))}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export const Route = createFileRoute('/about')({
    component: AboutPage,
    loader: async ({ context }) => {
        const [, websiteSettings] = await Promise.allSettled([
            context.apiUtils.webContent.getAllPublic.ensureData(),
            context.apiUtils.websiteSettings.getPublic.ensureData(),
        ])
        return {
            websiteSettings: websiteSettings.status === 'fulfilled' ? websiteSettings.value : null,
        }
    },
    head: ({ loaderData }) => ({
        meta: buildOgMeta({
            title: 'Long-Y - Rólunk',
            url: `${import.meta.env.VITE_WEB_URL || ''}/about`,
            websiteSettings: loaderData?.websiteSettings,
        }),
    }),
})
